/**
 * GravityFalls Method Fields - TypeScript Type Definitions
 *
 * Extended type definitions for method fields expansion feature.
 * These extend the base types in index.ts with new field structures.
 *
 * @version 1.0.0
 * @date 2026-01-24
 */

import type React from 'react';

// =============================================================================
// NEW FIELD TYPES - Topics (Changed from Taxonomy to ACF Field)
// =============================================================================

/**
 * Topic item type
 * Topics are now stored as ACF repeater/multi-select field, not taxonomy
 */
export interface TopicItem {
  value: string; // The topic text (translatable)
}

/**
 * Topics field type - array of topic items
 */
export type Topics = TopicItem[];

// =============================================================================
// NEW FIELD TYPES - Learning Goals
// =============================================================================

/**
 * Learning goals field type
 * WYSIWYG content field, required for published methods
 */
export interface LearningGoalsField {
  /** Raw HTML content from WYSIWYG editor */
  content: string;
  /** Character count for validation (max 10,000) */
  characterCount?: number;
}

// =============================================================================
// NEW FIELD TYPES - Preparation Notes
// =============================================================================

/**
 * Preparation notes field type
 * WYSIWYG content field, optional
 */
export interface PreparationNotesField {
  /** Raw HTML content from WYSIWYG editor */
  content: string;
  /** Character count for validation (max 10,000) */
  characterCount?: number;
}

// =============================================================================
// NEW FIELD TYPES - Materials Needed
// =============================================================================

/**
 * Materials needed field type
 * Two-part structure: boolean flag + textarea
 */
export interface MaterialsNeededField {
  /** Flag indicating if materials are required */
  flag: boolean;
  /** Plain text list of materials (line-break separated) */
  content: string | null;
}

/**
 * Parsed materials list (for frontend display)
 */
export type MaterialsList = string[];

// =============================================================================
// NEW FIELD TYPES - Online Materials Link
// =============================================================================

/**
 * Online materials link field type
 * Two-part structure: boolean flag + URL
 */
export interface OnlineMaterialsField {
  /** Flag indicating if online materials exist */
  flag: boolean;
  /** URL to online materials */
  url: string | null;
}

// =============================================================================
// CONTRIBUTOR FIELD (Changed from Taxonomy to ACF Text Field)
// =============================================================================

/**
 * Contributor field type
 * Now stored as ACF text field instead of taxonomy
 */
export interface ContributorField {
  /** Contributor/organization name (max 255 chars) */
  value: string | null;
}

// =============================================================================
// TRANSLATABLE FIELD LABELS
// =============================================================================

/**
 * Field label keys for translation system
 */
export type FieldLabelKey =
  | 'title'
  | 'duration'
  | 'description'
  | 'module'
  | 'topics'
  | 'activity_types'
  | 'contributor'
  | 'learning_goals'
  | 'preparation'
  | 'materials'
  | 'resources'
  | 'download_materials';

/**
 * Field labels response type
 * Returned by GET /training-planner/v1/field-labels endpoint
 */
export interface FieldLabels {
  title: string;
  duration: string;
  description: string;
  module: string;
  topics: string;
  activity_types: string;
  contributor: string;
  learning_goals: string;
  preparation: string;
  materials: string;
  resources: string;
  download_materials: string;
}

/**
 * Default field labels (English)
 */
export const DEFAULT_FIELD_LABELS: FieldLabels = {
  title: 'Title',
  duration: 'Duration',
  description: 'Description',
  module: 'Module',
  topics: 'Topics',
  activity_types: 'Activity Types',
  contributor: 'Contributor',
  learning_goals: 'Learning Goals',
  preparation: 'Preparation',
  materials: 'Materials',
  resources: 'Resources',
  download_materials: 'Download Materials',
};

/**
 * Language code type (supported languages)
 */
export type LanguageCode = 'en' | 'de' | 'pl' | 'da' | 'uk' | 'cs' | 'tr' | 'ar' | 'nl' | 'es' | 'fr';

/**
 * Field labels API response
 */
export interface FieldLabelsResponse {
  success: boolean;
  data: FieldLabels;
  lang: LanguageCode;
  cached?: boolean;
}

// =============================================================================
// COMPONENT PROPS TYPES
// =============================================================================

/**
 * ExpandableSection component props
 */
export interface ExpandableSectionProps {
  /** Section title (from translated field labels) */
  title: string;
  /** Section content (HTML, markdown, or plain text) */
  content: string;
  /** Icon to display (optional) */
  icon?: React.ReactNode;
  /** Initial expanded state */
  defaultExpanded?: boolean;
  /** Whether content is HTML (uses dangerouslySetInnerHTML) - deprecated, use isMarkdown instead */
  isHtml?: boolean;
  /** Whether content is markdown (uses react-markdown for safe parsing) */
  isMarkdown?: boolean;
  /** Test ID for automated testing */
  testId?: string;
  /** Children components (alternative to content prop) */
  children?: React.ReactNode;
}

/**
 * ResourceLink component props
 */
export interface ResourceLinkProps {
  /** Section title (from translated field labels) */
  title: string;
  /** URL to open */
  url: string;
  /** Opens in new tab */
  external?: boolean;
  /** Test ID for automated testing */
  testId?: string;
}

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Character limits for fields
 */
export const FIELD_LIMITS = {
  LEARNING_GOALS_MAX: 10000,
  PREPARATION_NOTES_MAX: 10000,
  MATERIALS_NEEDED_MAX: 1000,
  CONTRIBUTOR_MAX: 255,
  TITLE_MAX: 255,
} as const;

/**
 * Validate learning goals field
 */
export function validateLearningGoals(content: string): { valid: boolean; error?: string } {
  if (!content || content.trim().length === 0) {
    return { valid: false, error: 'Learning goals are required' };
  }
  if (content.length > FIELD_LIMITS.LEARNING_GOALS_MAX) {
    return { valid: false, error: `Learning goals must be ${FIELD_LIMITS.LEARNING_GOALS_MAX} characters or less` };
  }
  return { valid: true };
}

/**
 * Validate materials needed field
 */
export function validateMaterialsNeeded(field: MaterialsNeededField): { valid: boolean; error?: string } {
  if (field.flag && field.content) {
    if (field.content.length > FIELD_LIMITS.MATERIALS_NEEDED_MAX) {
      return { valid: false, error: `Materials list must be ${FIELD_LIMITS.MATERIALS_NEEDED_MAX} characters or less` };
    }
  }
  return { valid: true };
}

/**
 * Validate contributor field
 */
export function validateContributor(value: string | null): { valid: boolean; error?: string } {
  if (value && value.length > FIELD_LIMITS.CONTRIBUTOR_MAX) {
    return { valid: false, error: `Contributor must be ${FIELD_LIMITS.CONTRIBUTOR_MAX} characters or less` };
  }
  return { valid: true };
}

/**
 * Parse materials content into array of items
 * Handles both newlines and HTML line break tags (<br>, <br/>, <br />)
 */
export function parseMaterialsList(content: string | null): MaterialsList {
  if (!content) return [];

  // Normalize HTML line breaks to newlines
  // Handles <br>, <br/>, <br /> variations
  let normalized = content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n');

  // Strip any remaining HTML tags
  normalized = normalized.replace(/<[^>]+>/g, '');

  return normalized
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => line.replace(/^[-*]\s+/, ''));
}

// =============================================================================
// METHOD DETAIL TYPE (Full method with all expanded fields)
// =============================================================================

/**
 * Method detail type - includes all expanded fields
 * Used for single method view (modal/detail page)
 */
export interface MethodDetail {
  id: number;
  title: string;
  excerpt: string;
  content?: string;
  duration_min: number;
  duration_max: number;
  url?: string;
  thumbnail_url?: string;
  modules?: Array<{ id: number; name: string; slug: string }>;
  topics?: TopicItem[];
  tags?: Array<{ id: number; name: string; slug: string }>;
  contributor?: ContributorField;
  learning_goals?: LearningGoalsField;
  preparation_notes?: PreparationNotesField | null;
  materials_needed?: MaterialsNeededField;
  online_materials?: OnlineMaterialsField;
  facilitator_material_url?: string | null; // B1: facilitator PDF per language
}

/**
 * API response for single method detail
 */
export interface MethodDetailResponse {
  success: boolean;
  data: MethodDetail;
  cached?: boolean;
}
